import React, { useCallback, useMemo } from 'react'
import createBbox from '@turf/bbox'
import styled from 'styled-components'
import PolygonEditor, {
  PolygonValidationResult,
  usePolygonEditor,
} from '~/components/googleMap/polygonEditor'
import useGoogleMaps from '~/prix/react/hooks/googleMaps'
import GoogleMap from '~/components/googleMap/map'
import { Feature, Polygon } from 'geojson'
import objectHash from 'object-hash'
import { ButtonVariants } from '~/components/buttonVariants'
import { WrapperButtons, Right } from '../styles/modals'

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 500px;
  max-height: 600px;

  > main {
    border-radius: ${({ theme }) => theme.borderRadius};
    border-bottom: 0px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    overflow: hidden;
  }

  .bottom {
    display: flex;
    flex-direction: row;
    padding: 16px;
    justify-content: space-between;
    align-items: center;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      flex-direction: column;
    }

    .right {
      display: flex;
      flex: 1;
      justify-content: flex-end;
      gap: ${({ theme }) => theme.padding(1)};
      align-items: center;
    }
  }
`

const ErrorMessage = styled.p`
  color: red;
  font-size: 12px;
  flex: 1;
  margin: 0;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    margin-top: 0;
    margin-bottom: 10px;
  }
`

export type DemarcationType = 'polygon'
export type DemarcationPoint = google.maps.LatLngLiteral & {
  id: string
}

export type FeaturePolygonWithHash = Feature<
  Polygon,
  {
    hash: string
  }
>

export default function ModalSpaceDemarcation({
  onCloseModalDemarcation,
  setPolygonDemarcation,
  polygonDemarcation,
}: {
  onCloseModalDemarcation: () => void
  setPolygonDemarcation: (polygon: FeaturePolygonWithHash | null) => void
  polygonDemarcation: FeaturePolygonWithHash | null
}) {
  const { google } = useGoogleMaps()

  const brazilCenter = useMemo(
    () => ({
      lat: -13.182533103756843,
      lng: -52.89874109295445,
    }),
    [],
  )

  const polygon = usePolygonEditor(polygonDemarcation ?? null)
  const handleConfirm = useCallback(() => {
    if (!polygon.polygon) {
      return
    }
    const hash = objectHash(polygon.polygon.geometry.coordinates)
    setPolygonDemarcation({
      ...polygon.polygon,
      properties: {
        hash,
      },
    })
    onCloseModalDemarcation()
  }, [polygon.polygon])

  const handleCancel = () => {
    onCloseModalDemarcation()
  }

  const onReset = useCallback(() => {
    if (polygon.points.length) {
      polygon.onReset()
    }
    setPolygonDemarcation(null)
  }, [polygon.points.length])

  const mapLocation = useMemo(() => {
    if (!google || !polygonDemarcation) {
      return {
        center: brazilCenter,
        zoom: 4,
      }
    }
    const bbox = createBbox(polygonDemarcation)
    const bounds: google.maps.LatLngBoundsLiteral = {
      west: bbox[0],
      south: bbox[1],
      east: bbox[2],
      north: bbox[3],
    }
    return {
      bounds,
    }
  }, [google, polygonDemarcation])
  return (
    <Wrapper>
      <GoogleMap onClick={polygon.onMapClick} mapType='hybrid' {...mapLocation}>
        <PolygonEditor {...polygon} />
      </GoogleMap>

      {(polygon.polygonIsValid as PolygonValidationResult).errorMessage && (
        <WrapperButtons key='error-message'>
          <ErrorMessage>
            {(polygon.polygonIsValid as PolygonValidationResult).errorMessage}
          </ErrorMessage>
        </WrapperButtons>
      )}

      <WrapperButtons key='buttons'>
        <ButtonVariants
          onClick={onReset}
          disabled={!onReset}
          variant='transparent'
          label='Limpar'
          size='sm'
        />

        <ButtonVariants
          onClick={handleCancel}
          variant='primary'
          label='Cancelar'
          size='sm'
          style={{ height: '30px' }}
        />

        <ButtonVariants
          variant='primary'
          label='Selecionar'
          size='sm'
          onClick={handleConfirm}
          disabled={
            !polygon.polygon || (polygon.polygonIsValid as PolygonValidationResult).status === false
          }
          style={{ height: '30px' }}
        />
      </WrapperButtons>
    </Wrapper>
  )
}
